import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { modifier } from 'ember-modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import CloseButton from 'garaje/components/close-button';
import ButtonGroup from 'garaje/components/ui/button-group';
import UiCard from 'garaje/components/ui/card';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';

export interface DiffItem {
  label: string;
  from: string;
  fromPillClass: string;
  to: string;
  toPillClass: string;
}

export interface DiffGroup {
  heading: string;
  items: DiffItem[];
}

interface Signature {
  Args: {
    isOpen: boolean;
    groups: DiffGroup[];
    onConfirm: () => void;
    onCancel: () => void;
  };
}

export default class HrisDashboardSaveConfirmModal extends Component<Signature> {
  titleId = `${guidFor(this)}-title`;

  focusCancel = modifier((el: Element) => {
    if (el instanceof HTMLElement) el.focus();
  });

  handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.args.onCancel();
  };

  <template>
    {{#if @isOpen}}
      <ModalDialog
        @clickOutsideToClose={{false}}
        @targetAttachment="center"
        @translucentOverlay={{true}}
        @onClose={{@onCancel}}
        @containerClass="!max-w-lg"
      >
        <FocusTrapper>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={{this.titleId}}
            class="relative p-8"
            data-test-hris-save-confirm-modal
            {{on "keydown" this.handleKeydown}}
          >

            {{! Header }}
            <div class="flex items-start gap-3 mb-4">
              <div class="shrink-0 w-9 h-9 rounded-full bg-papaya-10 flex items-center justify-center text-papaya-40">
                {{svgJar "ui-caution" height="18" width="18"}}
              </div>
              <div>
                <h2 id={{this.titleId}} class="text-xl font-semibold text-carbon-80 m-0">
                  Save changes?
                </h2>
                <p class="mt-1 text-sm text-carbon-60 m-0">
                  You're switching steps to
                  <strong>Live</strong>
                  mode. Live steps make real changes in connected systems when a termination runs.
                </p>
              </div>
            </div>

            {{! Diff list }}
            <UiCard class="mt-5 !p-0 border-gray-200 overflow-hidden">
              <div
                class="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-carbon-40 uppercase tracking-wide"
              >
                Review your changes
              </div>
              <ul class="divide-y divide-gray-100 m-0 p-0 list-none">
                {{#each @groups as |group|}}
                  {{#if group.heading}}
                    <li class="px-4 py-2 bg-gray-50">
                      <span class="text-xs font-semibold text-carbon-50 uppercase tracking-wide">
                        {{group.heading}}
                      </span>
                    </li>
                  {{/if}}
                  {{#each group.items as |item|}}
                    <li class="flex items-center justify-between gap-3 px-4 py-2.5">
                      <span class="text-sm text-carbon-70 flex-1 min-w-0 truncate">{{item.label}}</span>
                      <span class="flex items-center gap-1.5 shrink-0">
                        <span
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full
                            {{item.fromPillClass}}"
                        >
                          {{item.from}}
                        </span>
                        {{svgJar "carot-right" height="10" width="10" class="text-carbon-30"}}
                        <span
                          class="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full
                            {{item.toPillClass}}"
                        >
                          {{item.to}}
                        </span>
                      </span>
                    </li>
                  {{/each}}
                {{/each}}
              </ul>
            </UiCard>

            {{! Footer }}
            <div class="pt-6 flex justify-end">
              <ButtonGroup>
                <button
                  type="button"
                  class="btn btn--small btn--outline hover:bg-carbon-5"
                  data-test-hris-save-confirm-cancel
                  {{this.focusCancel}}
                  {{on "click" @onCancel}}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn--small btn--red-outline"
                  data-test-hris-save-confirm-ok
                  {{on "click" @onConfirm}}
                >
                  Save changes
                </button>
              </ButtonGroup>
            </div>

            <CloseButton
              class="right-4 top-4 !bg-white !p-0 w-10 h-10"
              aria-label="Close"
              data-test-hris-save-confirm-close
              @onClick={{@onCancel}}
            />
          </div>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}
  </template>
}
